import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ChartBarIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import apiClient from '../../services/apiClient'
import { HomeworkSubmission } from '../../services/homeworkApi'
import AdminStudentCaseStudyModal, {
  CaseStudyViewTarget,
} from '../../components/homework/AdminStudentCaseStudyModal'

const STATUS_LABEL: Record<string, string> = {
  submitted: 'Đã nộp',
  in_review: 'Đang chấm',
  completed: 'Hoàn thành',
  returned: 'Trả bài',
}

export default function AdminHomeworkHubPage() {
  const [rows, setRows] = useState<HomeworkSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [caseTarget, setCaseTarget] = useState<CaseStudyViewTarget>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(t)
  }, [q])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const res = await apiClient.get('/admin/homework-submissions', {
          params: {
            q: debouncedQ || undefined,
            status: status || undefined,
          },
        })
        if (!cancelled) setRows(res.data?.data || [])
      } catch (e: any) {
        if (!cancelled) toast.error(e?.message || 'Không tải được bài làm')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedQ, status])

  const countLabel = useMemo(() => `${rows.length} bài làm`, [rows.length])

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="text-2xl font-bold">Quản lý bài làm học viên</h1>
          <p className="text-sm text-m2-on-surface-variant mt-1">
            Tra cứu bài BTVN đã nộp trên mọi khóa học. {countLabel}
          </p>
        </div>
        <Link
          to="/admin/homework-reports"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-m2-primary/30 text-m2-primary text-sm font-medium hover:bg-m2-primary/10"
          title="Báo cáo kết quả"
        >
          <ChartBarIcon className="h-5 w-5" />
          Báo cáo kết quả
        </Link>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 mt-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tìm tên HV, email, BTVN, khóa, lớp..."
          className="flex-1 min-w-[220px] border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
        >
          <option value="">Mọi trạng thái</option>
          <option value="submitted">Đã nộp</option>
          <option value="in_review">Đang chấm</option>
          <option value="completed">Hoàn thành</option>
          <option value="returned">Trả bài</option>
        </select>
      </div>

      {loading ? (
        <p className="text-m2-on-surface-variant">Đang tải...</p>
      ) : rows.length === 0 ? (
        <p className="text-m2-on-surface-variant">Không có bài làm phù hợp.</p>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-m2-surface-high text-left">
              <tr>
                <th className="px-3 py-2">Học viên</th>
                <th className="px-3 py-2">Khóa / BTVN</th>
                <th className="px-3 py-2">Lớp</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">AI</th>
                <th className="px-3 py-2">Nộp lúc</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.studentName || `#${r.userId}`}</div>
                    <div className="text-xs text-m2-on-surface-variant">{r.studentEmail}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.homeworkName}</div>
                    <div className="text-xs text-m2-on-surface-variant">
                      {r.courseCode} — {r.courseName}
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.classroomName || r.classroomId}</td>
                  <td className="px-3 py-2">{STATUS_LABEL[r.status] || r.status}</td>
                  <td className="px-3 py-2">
                    {r.aiScanStatus}
                    {r.pendingAiCount ? (
                      <span className="ml-1 text-violet-600">({r.pendingAiCount} chờ duyệt)</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {r.submittedAt ? new Date(r.submittedAt).toLocaleString('vi-VN') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {r.caseStudyId ? (
                        <button
                          type="button"
                          title={r.caseStudyName ? `Case study: ${r.caseStudyName}` : 'Xem case study'}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                          onClick={() =>
                            setCaseTarget({
                              userId: Number(r.userId),
                              courseId: r.courseId ? Number(r.courseId) : null,
                              studentName: r.studentName,
                            })
                          }
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                      <Link
                        to={`/admin/courses/${r.courseId}/homeworks/${r.homeworkId}/submissions/${r.id}`}
                        className="text-m2-primary hover:underline whitespace-nowrap"
                      >
                        Xem / chấm
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminStudentCaseStudyModal target={caseTarget} onClose={() => setCaseTarget(null)} />
    </div>
  )
}
